import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { Body5, Body7, Container } from '@ui';
import { SkeletonButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { RUN_TERMINAL_STATUSES } from '@services/fmdc';
import { RunStatusBadge } from './RunStatusBadge';
import { RerunConfirm } from './RerunConfirm';
import { fmdcPaths } from './fmdcUrl';
import {
  EM_DASH,
  deriveRunDisplayStatus,
  formatCount,
  formatDateTime,
  formatElapsed,
  formatRelative,
  formatRunStatus,
} from './format';
import { HeaderCell, HeaderRow, Row, Table, TableScroll, rowKeyHandler } from './styles';

// Source | Issuer | Status | Review | Extracted · Unmapped | Template | Created | Rerun
const GRID_COLUMNS =
  'minmax(220px, 2.4fr) minmax(130px, 1.2fr) minmax(150px, 1.3fr) minmax(120px, 1fr) minmax(130px, 1.1fr) minmax(80px, 0.7fr) minmax(110px, 1fr) minmax(80px, 0.6fr)';

// aria-label on role="button" REPLACES the row's inner text, so this string is the entire row for
// a screen reader. Clauses are appended, never prepended: the leading "<source> run #4" is what row
// lookups match on.
export const rowAriaLabel = (run) => {
  const parts = [
    `${run.source_name || 'Model'} run #${run.id}`,
    `status ${formatRunStatus(deriveRunDisplayStatus(run))}`,
  ];
  if (run.review) parts.push(`${formatCount(run.review.open)} open`);
  if (run.issuer_id) parts.push(`issuer ${run.issuer_id}`);
  return parts.join(', ');
};

// The row is dense; a CTA-sized SkeletonButton (12px 24px) would set its height. The descendant
// `button { padding }` idiom is feedView's FilterChip one, with the same caveat — a `size` prop on
// SkeletonButton is the real fix.
const CompactButton = styled.div`
  button {
    padding: 6px 14px;
  }
`;

const reviewSummary = (review) => {
  if (!review) return EM_DASH;
  return `${formatCount(review.open)} open / ${formatCount(review.resolved)} resolved · ${formatCount(
    review.dismissed
  )} dismissed`;
};

export const RunsTable = ({ runs, now = Date.now(), onReload }) => {
  const navigate = useNavigate();
  const [rerunTarget, setRerunTarget] = React.useState(null);

  const openRun = (run) => navigate(fmdcPaths.run(ROUTES.fmdc, run.id));

  return (
    <>
      <TableScroll>
        <Table $minWidth={1040}>
          <HeaderRow $columns={GRID_COLUMNS}>
            <HeaderCell>Source</HeaderCell>
            <HeaderCell>Issuer</HeaderCell>
            <HeaderCell>Status</HeaderCell>
            <HeaderCell>Review open / resolved</HeaderCell>
            <HeaderCell>Extracted · Unmapped</HeaderCell>
            <HeaderCell>Template</HeaderCell>
            <HeaderCell>Created</HeaderCell>
            <span />
          </HeaderRow>

          {runs.map((run) => {
            const canRerun = run.rerunnable || run.stuck;
            return (
              <Row
                key={run.id}
                $columns={GRID_COLUMNS}
                $interactive
                role="button"
                tabIndex={0}
                onClick={() => openRun(run)}
                onKeyDown={rowKeyHandler(() => openRun(run))}
                aria-label={rowAriaLabel(run)}
              >
                <div>
                  <Body5 bold truncate title={run.source_name}>
                    {run.source_name || EM_DASH}
                  </Body5>
                  <Container top={4}>
                    <Body7 isLowContrast>#{run.id}</Body7>
                  </Container>
                </div>
                <div>
                  <Body7 truncate title={run.issuer_id}>
                    {run.issuer_id || EM_DASH}
                  </Body7>
                  {run.issuer_name && (
                    <Container top={4}>
                      <Body7 isLowContrast truncate title={run.issuer_name}>
                        {run.issuer_name}
                      </Body7>
                    </Container>
                  )}
                </div>
                <div>
                  <RunStatusBadge run={run} />
                  {/* Elapsed while the run has not finished — a stuck run included, where "6h 02m" IS
                      the story. Once it reads review / loadable / error the stage is history. */}
                  {!RUN_TERMINAL_STATUSES.includes(run.status) && (
                    <Container top={4}>
                      <Body7 isLowContrast>{formatElapsed(run.created_at, now)}</Body7>
                    </Container>
                  )}
                </div>
                <div>
                  <Body7 isLowContrast>{reviewSummary(run.review)}</Body7>
                </div>
                <div>
                  <Body7 isLowContrast>
                    {formatCount(run.counts?.extraction_rows)} · {formatCount(run.counts?.unmapped_rows)}
                  </Body7>
                </div>
                <div>
                  <Body7 isLowContrast>{run.template_version || EM_DASH}</Body7>
                </div>
                <div>
                  <Body7 isLowContrast title={formatDateTime(run.created_at)}>
                    {formatRelative(run.created_at)}
                  </Body7>
                </div>
                <div>
                  {canRerun && (
                    <CompactButton
                      // The row is itself a button; the rerun button must not also open it.
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <SkeletonButton
                        onClick={() => setRerunTarget(run)}
                        buttonProps={{ 'aria-label': `Rerun run #${run.id}` }}
                      >
                        Rerun
                      </SkeletonButton>
                    </CompactButton>
                  )}
                </div>
              </Row>
            );
          })}
        </Table>
      </TableScroll>

      {rerunTarget && (
        <RerunConfirm
          run={rerunTarget}
          onDone={() => {
            setRerunTarget(null);
            if (onReload) onReload();
          }}
          onCancel={() => setRerunTarget(null)}
        />
      )}
    </>
  );
};
